import { WebApiService } from "../../../plugins/webApiService";
import { Events } from "../../../plugins/events";
import EventToAsyncDecorator from "../../../plugins/eventToAsyncDecorator";
import WsResponseModelT from "../../../SchemaBuilderServer/Models/WsResponseModelT";
import PaginationRequest from "../../../Common/RequestModels/PaginationRequest";
import PaginationResponseT from "../../../Common/ResponseModels/PaginationResponseT";
import ReferralRuleItem from "../Interfaces/Models/Referral/ReferralRuleItem";
import ReferralTrackingItem from "../Interfaces/Models/Referral/ReferralTrackingItem";
import SearchReferralRuleRequest from "../Interfaces/Models/Referral/SearchReferralRuleRequest";
import SearchReferralTrackingRequest from "../Interfaces/Models/Referral/SearchReferralTrackingRequest";
import UpsertReferralRuleRequest from "../Interfaces/Models/Referral/UpsertReferralRuleRequest";
import {
  buildUpsertReferralRulePayload,
  isReferralRuleUpsertTriggerSupported,
  toReferralTrackingStatusApiValue,
  toReferralTriggerApiValue,
} from "../Interfaces/Models/Referral/referralRuleMapper";

export default class ReferralService {
  webApiService: WebApiService;
  UpsertRuleEvent = new Events<WsResponseModelT<Boolean>>();
  GetRuleEvent = new Events<WsResponseModelT<ReferralRuleItem>>();
  GetRuleListEvent = new Events<WsResponseModelT<PaginationResponseT<ReferralRuleItem>>>();
  DeleteRuleEvent = new Events<WsResponseModelT<Boolean>>();
  ToggleRuleEvent = new Events<WsResponseModelT<Boolean>>();
  GetTrackingListEvent = new Events<WsResponseModelT<PaginationResponseT<ReferralTrackingItem>>>();

  constructor(webApiService: WebApiService) {
    const self = this;
    this.webApiService = webApiService;
    webApiService.on("ReferralService", "UpsertRule", (res: WsResponseModelT<Boolean>) => {
      self.UpsertRuleEvent.trigger(res);
    });
    webApiService.on("ReferralService", "GetRule", (res: WsResponseModelT<ReferralRuleItem>) => {
      self.GetRuleEvent.trigger(res);
    });
    webApiService.on("ReferralService", "GetRuleList", (res: WsResponseModelT<PaginationResponseT<ReferralRuleItem>>) => {
      self.GetRuleListEvent.trigger(res);
    });
    webApiService.on("ReferralService", "DeleteRule", (res: WsResponseModelT<Boolean>) => {
      self.DeleteRuleEvent.trigger(res);
    });
    webApiService.on("ReferralService", "ToggleRule", (res: WsResponseModelT<Boolean>) => {
      self.ToggleRuleEvent.trigger(res);
    });
    webApiService.on("ReferralService", "GetTrackingList", (res: WsResponseModelT<PaginationResponseT<ReferralTrackingItem>>) => {
      self.GetTrackingListEvent.trigger(res);
    });
  }

  UpsertRule(request: UpsertReferralRuleRequest = null) {
    if (!request) {
      this.webApiService.send("ReferralService", "UpsertRule", request);
      return;
    }

    if (!isReferralRuleUpsertTriggerSupported(request.Trigger)) {
      this.UpsertRuleEvent.trigger({
        Class: "ReferralService",
        Method: "UpsertRule",
        ErrorMessage: "Этот тип условия пока не поддерживается сервером.",
        IsSuccess: false,
        Value: null,
      });
      return;
    }

    const payload = buildUpsertReferralRulePayload(request);
    this.webApiService.send(
      "ReferralService",
      "UpsertRule",
      {
        RuleId: payload.ruleId,
        Trigger: payload.trigger,
        Threshold: payload.threshold,
        WindowDays: payload.windowDays,
        RewardType: payload.rewardType,
        RewardDays: payload.rewardDays,
      }
    );
  }

  GetRule(ruleId: number = 0) {
    this.webApiService.send("ReferralService", "GetRule", { ruleId });
  }

  GetRuleList(request: any = null) {
    this.webApiService.send("ReferralService", "GetRuleList", this.toRuleListRequest(request));
  }

  DeleteRule(ruleId: number = 0) {
    this.webApiService.send("ReferralService", "DeleteRule", { ruleId });
  }

  ToggleRule(request: { ruleId: number; isActive: boolean } = null) {
    this.webApiService.send("ReferralService", "ToggleRule", request);
  }

  GetTrackingList(request: any = null) {
    this.webApiService.send("ReferralService", "GetTrackingList", this.toTrackingListRequest(request));
  }

  async upsertRuleAsync(request: UpsertReferralRuleRequest = null): Promise<Boolean> {
    return EventToAsyncDecorator.getInstance().bind(this.UpsertRule.bind(this), this.UpsertRuleEvent)(request);
  }

  async getRuleAsync(ruleId: number = 0): Promise<ReferralRuleItem> {
    return EventToAsyncDecorator.getInstance().bind(this.GetRule.bind(this), this.GetRuleEvent)(ruleId);
  }

  async getRuleListAsync(request: SearchReferralRuleRequest = null): Promise<PaginationResponseT<ReferralRuleItem>> {
    const response = await EventToAsyncDecorator.getInstance().bind(this.GetRuleList.bind(this), this.GetRuleListEvent)(request);
    return this.toPaginationResponse(response);
  }

  async deleteRuleAsync(ruleId: number = 0): Promise<Boolean> {
    return EventToAsyncDecorator.getInstance().bind(this.DeleteRule.bind(this), this.DeleteRuleEvent)(ruleId);
  }

  async toggleRuleAsync(request: { ruleId: number; isActive: boolean } = null): Promise<Boolean> {
    return EventToAsyncDecorator.getInstance().bind(this.ToggleRule.bind(this), this.ToggleRuleEvent)(request);
  }

  async getTrackingListAsync(request: SearchReferralTrackingRequest = null): Promise<PaginationResponseT<ReferralTrackingItem>> {
    const response = await EventToAsyncDecorator.getInstance().bind(this.GetTrackingList.bind(this), this.GetTrackingListEvent)(request);
    return this.toPaginationResponse(response);
  }

  private toPageRequest(request: any) {
    if (!request) {
      return request;
    }

    const result = { ...request };
    const pagination = result.Pagination || result.pagination;
    if (pagination) {
      result.Page = new PaginationRequest({
        Skip: pagination.Skip == null ? pagination.skip : pagination.Skip,
        Take: pagination.Take == null ? pagination.take : pagination.Take,
      });
      delete result.Pagination;
      delete result.pagination;
    }
    delete result.Search;
    delete result.Sort;
    return result;
  }

  private toRuleListRequest(request: SearchReferralRuleRequest | any) {
    const result = this.toPageRequest(request);
    if (!result) {
      return result;
    }

    if (Object.prototype.hasOwnProperty.call(result, "Trigger")) {
      result.Trigger = toReferralTriggerApiValue(result.Trigger);
    }

    return result;
  }

  private toTrackingListRequest(request: SearchReferralTrackingRequest | any) {
    const result = this.toPageRequest(request);
    if (!result) {
      return result;
    }

    if (Object.prototype.hasOwnProperty.call(result, "Trigger")) {
      result.Trigger = toReferralTriggerApiValue(result.Trigger);
    }

    if (Object.prototype.hasOwnProperty.call(result, "Status")) {
      result.Status = toReferralTrackingStatusApiValue(result.Status);
    }

    return result;
  }

  private toPaginationResponse<T>(response: any): PaginationResponseT<T> {
    if (!response) {
      return new PaginationResponseT<T>({ Items: [], Count: 0 });
    }

    return new PaginationResponseT<T>({
      Items: response.Items || [],
      Count: response.Count || (response.Page && response.Page.Count) || 0,
    });
  }
}
